/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';
import { syncService, SyncJob } from '../services/syncService';
import { toaster } from '@components/ui/toaster';

interface SyncContextProps {
  activeSyncJobs: SyncJob[];
  isSyncing: boolean;
  addJob: (jobId: string) => void;
  removeJob: (jobId: string) => void;
  getJobStatus: (jobId: string) => SyncJob | undefined;
}

const SyncContext = createContext<SyncContextProps | undefined>(undefined);

export const SyncProvider = ({ children }: { children: ReactNode }) => {
  const [activeSyncJobs, setActiveSyncJobs] = useState<SyncJob[]>([]);
  const [jobIdsToTrack, setJobIdsToTrack] = useState<Set<string>>(new Set());

  const addJob = useCallback((jobId: string) => {
    setJobIdsToTrack(prev => new Set([...prev, jobId]));
  }, []);

  const removeJob = useCallback((jobId: string) => {
    setJobIdsToTrack(prev => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });
    setActiveSyncJobs(prev => prev.filter(job => job.jobId !== jobId));
  }, []);

  const getJobStatus = useCallback((jobId: string) => {
    return activeSyncJobs.find(job => job.jobId === jobId);
  }, [activeSyncJobs]);

  useEffect(() => {
    if (jobIdsToTrack.size === 0) {
      return;
    }

    const pollJobs = async () => {
      const updatedJobs: SyncJob[] = [];

      for (const jobId of Array.from(jobIdsToTrack)) {
        try {
          const job = await syncService.getSyncJobStatus(jobId);
          updatedJobs.push(job);

          const prevJob = activeSyncJobs.find(j => j.jobId === jobId);

          if (job.status === 'completed' && prevJob?.status !== 'completed') {
            toaster.create({
              title: 'Sync completed',
              description: job.progress || 'Successfully synced to providers',
              type: 'success',
              duration: 3000,
            });
            setTimeout(() => removeJob(jobId), 3000);
          } else if (job.status === 'failed' && prevJob?.status !== 'failed') {
            toaster.create({
              title: 'Sync failed',
              description: job.error || 'Failed to sync to providers',
              type: 'error',
              duration: 5000,
            });
            setTimeout(() => removeJob(jobId), 5000);
          } else if (job.status === 'processing' && prevJob?.status !== 'processing') {
            toaster.create({
              title: 'Syncing',
              description: job.progress || 'Syncing to providers...',
              type: 'info',
              duration: 2000,
            });
          }
        } catch (error) {
          console.error(`Failed to fetch job ${jobId}:`, error);
          removeJob(jobId);
        }
      }

      setActiveSyncJobs(updatedJobs);
    };

    pollJobs();

    const interval = setInterval(pollJobs, 3000);

    return () => clearInterval(interval);
  }, [jobIdsToTrack, activeSyncJobs, removeJob]);

  const isSyncing = activeSyncJobs.some(
    job => job.status === 'pending' || job.status === 'processing'
  );

  return (
    <SyncContext.Provider
      value={{
        activeSyncJobs,
        isSyncing,
        addJob,
        removeJob,
        getJobStatus,
      }}
    >
      {children}
    </SyncContext.Provider>
  );
};

export const useSyncContext = () => {
  const context = useContext(SyncContext);
  if (!context) {
    throw new Error('useSyncContext must be used within a SyncProvider');
  }
  return context;
};
